import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Phone as PhoneIcon,
  PhoneMissed as MissedIcon,
  DevicesOther as SipIcon,
  Close as CloseIcon,
  VoicemailOutlined as VoicemailIcon,
  CallMade as OutgoingIcon,
  CallReceived as IncomingIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  AccessTime as TimeIcon,
  PhoneCallback as RingingIcon,
  PhoneInTalk as AnsweredIcon,
  CallEnd as EndedIcon,
  Info as InfoIcon,
  Block as RejectedIcon,
  Forward as RedirectIcon,
  RecordVoiceOver as TalkIcon,
  LockClock as CredentialIcon,
  PowerOff as DeactivatedIcon,
  HowToReg as ActivatedIcon,
  FiberSmartRecord as RecordIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { telephonyApi } from '../../services/apiClient';
import SipEndpointsTab from './SipEndpointsTab';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

const statusColors = {
  answered: { bg: '#E8F5E9', color: '#2E7D32', label: 'Answered' },
  missed: { bg: '#FFEBEE', color: '#C62828', label: 'Missed' },
  voicemail: { bg: '#FFF8E1', color: '#F57F17', label: 'Voicemail' },
  ringing: { bg: '#E3F2FD', color: '#1565C0', label: 'Ringing' },
  rejected: { bg: '#F5F5F5', color: '#616161', label: 'Rejected' },
  failed: { bg: '#F5F5F5', color: '#616161', label: 'Failed' },
};

const directionColors = {
  inbound: { bg: '#E3F2FD', color: '#1565C0', label: 'Inbound' },
  outbound: { bg: '#F3E5F5', color: '#7B1FA2', label: 'Outbound' },
  internal: { bg: '#F5F5F5', color: '#616161', label: 'Internal' },
};

export default function TelephonyManagement() {
  const { user } = useAuth();
  const { t } = useThemeSettings();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState(0);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t ? t('common.admin_only') : 'This page is only accessible to administrators.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
        <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {t ? t('Telephony.title') : 'Telephony'}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t ? t('Telephony.tab_call_logs') : 'Call Logs'} />
        <Tab label={t ? t('Telephony.tab_sip_devices') : 'SIP Devices'} />
        <Tab label={t ? t('Telephony.tab_sip_endpoints') : 'SIP Endpoints'} />
        <Tab label={t ? t('Telephony.tab_overview') : 'Overview'} />
      </Tabs>

      <TabPanel value={tab} index={0}><CallLogsTab t={t} /></TabPanel>
      <TabPanel value={tab} index={1}><SipDevicesTab t={t} /></TabPanel>
      <TabPanel value={tab} index={2}><SipEndpointsTab t={t} /></TabPanel>
      <TabPanel value={tab} index={3}><OverviewTab t={t} /></TabPanel>
    </Box>
  );
}

// ─── Hangup cause code descriptions ────────────────────────────
const hangupCauses = {
  0: 'Not defined',
  1: 'Unallocated number',
  3: 'No route to destination',
  16: 'Normal call clearing',
  17: 'User busy',
  18: 'No user responding',
  19: 'No answer from user',
  21: 'Call rejected',
  27: 'Destination out of order',
  31: 'Normal, unspecified',
  34: 'No circuit available',
  38: 'Network out of order',
  41: 'Temporary failure',
  44: 'Requested channel unavailable',
  50: 'Facility not subscribed',
  58: 'Bearer capability not available',
  65: 'Bearer capability not implemented',
  127: 'Interworking, unspecified',
};

function getHangupLabel(cause) {
  if (cause == null || cause === '') return null;
  const code = Number(cause);
  return hangupCauses[code] || `Code ${cause}`;
}

function formatFullDateTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatDuration(seconds) {
  if (seconds == null || seconds === 0) return '-';
  const s = Number(seconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

// ─── Expandable call detail row ─────────────────────────────────
function CallDetailRow({ call }) {
  const hangupLabel = getHangupLabel(call.hangupCause);
  const hangupCode = Number(call.hangupCause);
  const talkTime = call.durationSeconds > 0 && call.answeredAt
    ? call.durationSeconds - (call.ringDurationSeconds || 0)
    : 0;

  // Build timeline steps based on call scenario
  const timelineSteps = [];

  // Step 1: Always show "Call initiated"
  timelineSteps.push({
    icon: <TimeIcon fontSize="small" />,
    color: '#1565C0',
    label: 'Call initiated',
    value: formatFullDateTime(call.startedAt),
  });

  // Scenario: Rejected (hangup cause 21 or status rejected)
  if (call.status === 'rejected' || hangupCode === 21) {
    timelineSteps.push({
      icon: <RejectedIcon fontSize="small" />,
      color: '#C62828',
      label: 'Call rejected by callee',
      value: hangupLabel || 'Rejected',
    });
  }
  // Scenario: Answered call
  else if (call.status === 'answered' && call.answeredAt) {
    if (call.ringDurationSeconds > 0) {
      timelineSteps.push({
        icon: <RingingIcon fontSize="small" />,
        color: '#F57F17',
        label: 'Ringing duration',
        value: formatDuration(call.ringDurationSeconds),
      });
    }
    timelineSteps.push({
      icon: <AnsweredIcon fontSize="small" />,
      color: '#2E7D32',
      label: 'Picked up',
      value: formatFullDateTime(call.answeredAt),
    });
    if (talkTime > 0) {
      timelineSteps.push({
        icon: <TalkIcon fontSize="small" />,
        color: '#2E7D32',
        label: 'Talk time',
        value: formatDuration(talkTime),
      });
    }
  }
  // Scenario: Voicemail — no answer, redirected to voicemail
  else if (call.status === 'voicemail') {
    if (call.ringDurationSeconds > 0) {
      timelineSteps.push({
        icon: <RingingIcon fontSize="small" />,
        color: '#F57F17',
        label: 'Ringing duration',
        value: formatDuration(call.ringDurationSeconds),
      });
    }
    timelineSteps.push({
      icon: <MissedIcon fontSize="small" />,
      color: '#C62828',
      label: 'No answer',
      value: hangupLabel || 'Callee did not pick up',
    });
    timelineSteps.push({
      icon: <RedirectIcon fontSize="small" />,
      color: '#F57F17',
      label: 'Redirected to voicemail',
      value: 'Call forwarded to voicemail system',
    });
    if (call.hasVoicemail) {
      timelineSteps.push({
        icon: <VoicemailIcon fontSize="small" />,
        color: '#F57F17',
        label: 'Voicemail recorded',
        value: (
          <AuthenticatedAudio
            callId={call.id}
            style={{ width: '100%', maxWidth: 300, height: 32 }}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      });
    }
  }
  // Scenario: Missed / Failed — no answer, no voicemail
  else if (call.status === 'missed' || call.status === 'failed') {
    if (call.ringDurationSeconds > 0) {
      timelineSteps.push({
        icon: <RingingIcon fontSize="small" />,
        color: '#F57F17',
        label: 'Ringing duration',
        value: formatDuration(call.ringDurationSeconds),
      });
    }
    let missedLabel = 'Not answered';
    let missedValue = 'Call was not picked up';
    if (hangupCode === 16 || hangupCode === 19) {
      missedValue = 'Caller hung up';
    } else if (hangupCode === 17) {
      missedLabel = 'User busy';
      missedValue = 'Callee was busy';
    } else if (hangupCode === 18) {
      missedLabel = 'No response';
      missedValue = 'No response from callee device';
    } else if (call.status === 'failed') {
      missedLabel = 'Call failed';
      missedValue = hangupLabel || 'Connection could not be established';
    }
    timelineSteps.push({
      icon: <MissedIcon fontSize="small" />,
      color: '#C62828',
      label: missedLabel,
      value: missedValue,
    });
  }

  // Final step: Always show "Call ended" if we have endedAt
  if (call.endedAt) {
    timelineSteps.push({
      icon: <EndedIcon fontSize="small" />,
      color: '#616161',
      label: 'Call ended',
      value: formatFullDateTime(call.endedAt),
    });
  }

  return (
    <Box sx={{ py: 2, px: 1 }}>
      <Grid container spacing={3}>
        {/* Timeline */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Call Timeline
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {timelineSteps.map((step, idx) => (
              <TimelineItem
                key={idx}
                icon={step.icon}
                color={step.color}
                label={step.label}
                value={step.value}
              />
            ))}
          </Box>
        </Grid>

        {/* Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <DetailItem label="Call ID" value={call.callId || call.id} mono />
            <DetailItem label="Total duration" value={formatDuration(call.durationSeconds)} />
            {call.ringDurationSeconds > 0 && (
              <DetailItem label="Ring duration" value={formatDuration(call.ringDurationSeconds)} />
            )}
            {talkTime > 0 && (
              <DetailItem label="Talk time" value={formatDuration(talkTime)} />
            )}
            {hangupLabel && (
              <DetailItem label="Hangup cause" value={`${hangupLabel} (${call.hangupCause})`} />
            )}
          </Box>

          {/* Recording audio (not voicemail — voicemail is inline in timeline) */}
          {call.hasRecording && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                Recording
              </Typography>
              <AuthenticatedAudio
                callId={call.id}
                audioType="recording"
                style={{ width: '100%', maxWidth: 400 }}
              />
            </Box>
          )}

          {/* Voicemail audio fallback for non-voicemail status calls that somehow have VM */}
          {call.status !== 'voicemail' && call.hasVoicemail && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                Voicemail
              </Typography>
              <AuthenticatedAudio
                callId={call.id}
                audioType="voicemail"
                style={{ width: '100%', maxWidth: 400 }}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

function TimelineItem({ icon, color, label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '50%',
        bgcolor: `${color}15`, color, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        mt: 0.25,
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>{label}</Typography>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );
}

function DetailItem({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 120 }}>{label}:</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? '0.8rem' : undefined }}>{value}</Typography>
    </Box>
  );
}

// ─── Authenticated Audio Player ──────────────────────────────
function AuthenticatedAudio({ callId, audioType, style, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoked = false;
    const fetchAudio = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await telephonyApi.getCallLogAudio(callId, audioType);
        if (revoked) return;
        const url = URL.createObjectURL(response.data);
        setBlobUrl(url);
      } catch {
        if (!revoked) setError(true);
      } finally {
        if (!revoked) setLoading(false);
      }
    };
    fetchAudio();
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [callId, audioType]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...style }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Loading audio...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error" sx={style}>
        Failed to load audio
      </Typography>
    );
  }

  if (!blobUrl) return null;

  return (
    <audio
      controls
      style={style}
      src={blobUrl}
      onClick={onClick}
    />
  );
}

// ─── Tab 1: Call Logs ─────────────────────────────────────────
function CallLogsTab({ t }) {
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [audioDialog, setAudioDialog] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [orderBy, setOrderBy] = useState('startedAt');
  const [order, setOrder] = useState('desc');

  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { limit: rowsPerPage, offset: page * rowsPerPage };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (directionFilter) params.direction = directionFilter;

      const { data } = await telephonyApi.getCallLogs(params);
      setCalls(data.calls || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load call logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, directionFilter]);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const sortedCalls = useMemo(() => {
    if (!orderBy) return calls;
    return [...calls].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      // Handle null/undefined
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      // Numeric fields
      if (orderBy === 'durationSeconds' || orderBy === 'ringDurationSeconds') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      // Date fields
      else if (orderBy === 'startedAt' || orderBy === 'answeredAt' || orderBy === 'endedAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      // String fields
      else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [calls, orderBy, order]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') { setPage(0); fetchCalls(); }
  };

  return (
    <>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t ? t('Telephony.search_placeholder') : 'Search name, extension, callId...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t ? t('Telephony.status') : 'Status'}</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="answered">Answered</MenuItem>
            <MenuItem value="missed">Missed</MenuItem>
            <MenuItem value="voicemail">Voicemail</MenuItem>
            <MenuItem value="ringing">Ringing</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t ? t('Telephony.direction') : 'Direction'}</InputLabel>
          <Select value={directionFilter} label="Direction" onChange={(e) => { setDirectionFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="inbound">Inbound</MenuItem>
            <MenuItem value="outbound">Outbound</MenuItem>
            <MenuItem value="internal">Internal</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={() => { setPage(0); fetchCalls(); }} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sortDirection={orderBy === 'startedAt' ? order : false}>
                <TableSortLabel active={orderBy === 'startedAt'} direction={orderBy === 'startedAt' ? order : 'asc'} onClick={() => handleSort('startedAt')}>
                  Time
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'callerName' ? order : false}>
                <TableSortLabel active={orderBy === 'callerName'} direction={orderBy === 'callerName' ? order : 'asc'} onClick={() => handleSort('callerName')}>
                  Caller
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'calleeName' ? order : false}>
                <TableSortLabel active={orderBy === 'calleeName'} direction={orderBy === 'calleeName' ? order : 'asc'} onClick={() => handleSort('calleeName')}>
                  Callee
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'direction' ? order : false}>
                <TableSortLabel active={orderBy === 'direction'} direction={orderBy === 'direction' ? order : 'asc'} onClick={() => handleSort('direction')}>
                  Direction
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'durationSeconds' ? order : false}>
                <TableSortLabel active={orderBy === 'durationSeconds'} direction={orderBy === 'durationSeconds' ? order : 'asc'} onClick={() => handleSort('durationSeconds')}>
                  Duration
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">VM</TableCell>
              <TableCell align="center">Recording</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : sortedCalls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No call logs found
                </TableCell>
              </TableRow>
            ) : sortedCalls.map((call) => {
              const sc = statusColors[call.status] || statusColors.failed;
              const dc = directionColors[call.direction] || directionColors.internal;
              const isExpanded = expandedRow === call.id;
              return (
                <React.Fragment key={call.id}>
                  <TableRow
                    hover
                    onClick={() => setExpandedRow(isExpanded ? null : call.id)}
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                  >
                    <TableCell sx={{ width: 40, px: 1 }}>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                      {new Date(call.startedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{call.callerName || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{call.callerExtension}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{call.calleeName || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{call.calleeExtension}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={dc.label}
                        size="small"
                        icon={call.direction === 'inbound' ? <IncomingIcon fontSize="small" /> : <OutgoingIcon fontSize="small" />}
                        sx={{ bgcolor: dc.bg, color: dc.color, fontWeight: 600, fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sc.label}
                        size="small"
                        sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600, fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                      {call.durationFormatted || '0:00'}
                    </TableCell>
                    <TableCell align="center">
                      {call.hasVoicemail && (
                        <Tooltip title="Has voicemail">
                          <VoicemailIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={
                        call.hasRecording
                          ? 'Play recording'
                          : call.status === 'missed' ? 'No recording — call was not answered'
                          : call.status === 'voicemail' ? 'No recording — went to voicemail'
                          : 'No recording available'
                      }>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!call.hasRecording}
                            onClick={(e) => { e.stopPropagation(); setAudioDialog({ ...call, audioType: 'recording' }); }}
                            sx={call.hasRecording ? { color: '#2E7D32' } : undefined}
                          >
                            <RecordIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, px: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ bgcolor: 'action.hover', px: 3, py: 0 }}>
                          <CallDetailRow call={call} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Audio Playback Dialog */}
      <Dialog open={!!audioDialog} onClose={() => setAudioDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{audioDialog?.audioType === 'recording' ? 'Recording' : 'Playback'} - Call #{audioDialog?.id}</span>
          <IconButton size="small" onClick={() => setAudioDialog(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {audioDialog && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {audioDialog.callerName || audioDialog.callerExtension} &rarr; {audioDialog.calleeName || audioDialog.calleeExtension}
              </Typography>
              <AuthenticatedAudio
                callId={audioDialog.id}
                audioType={audioDialog.audioType}
                style={{ width: '100%' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAudioDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── SIP session detail row (expandable) ────────────────────────
const sipEventConfig = {
  created:                  { icon: <TimeIcon fontSize="small" />,         color: '#1565C0', label: 'Session created' },
  activated:                { icon: <ActivatedIcon fontSize="small" />,    color: '#2E7D32', label: 'Activated' },
  deactivated:              { icon: <DeactivatedIcon fontSize="small" />,  color: '#C62828', label: 'Deactivated' },
  registered:               { icon: <AnsweredIcon fontSize="small" />,     color: '#2E7D32', label: 'Device registered' },
  unregistered:             { icon: <EndedIcon fontSize="small" />,        color: '#F57F17', label: 'Device unregistered' },
  credentials_regenerated:  { icon: <CredentialIcon fontSize="small" />,   color: '#7B1FA2', label: 'Credentials regenerated' },
  expired:                  { icon: <EndedIcon fontSize="small" />,        color: '#C62828', label: 'Session expired' },
  registration_timeout:     { icon: <EndedIcon fontSize="small" />,        color: '#E65100', label: 'Registration timeout' },
};

function SipSessionDetailRow({ session, history, loading }) {
  return (
    <Box sx={{ py: 2, px: 1 }}>
      <Grid container spacing={3}>
        {/* Session Info */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Session Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <DetailItem label="Session ID" value={session.id} mono />
            <DetailItem label="Extension" value={session.extension} mono />
            <DetailItem label="Person" value={session.personName || '-'} />
            <DetailItem label="Device" value={session.deviceName || '-'} />
            <DetailItem label="Created" value={formatFullDateTime(session.createdAt)} />
            <DetailItem label="Expires" value={formatFullDateTime(session.expiresAt)} />
            <DetailItem label="Last Registered" value={formatFullDateTime(session.lastRegisteredAt)} />
          </Box>
        </Grid>

        {/* Registration Timeline */}
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Registration History
          </Typography>
          {loading ? (
            <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No history recorded yet. State changes will appear here as the device registers and unregisters.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 300, overflowY: 'auto', pr: 1 }}>
              {history.map((entry) => {
                const cfg = sipEventConfig[entry.event] || { icon: <InfoIcon fontSize="small" />, color: '#616161', label: entry.event };
                const meta = entry.metadata;
                let detail = formatFullDateTime(entry.createdAt);
                if (entry.triggeredBy) detail += ` (by ${entry.triggeredBy})`;
                if (meta?.reason) detail += ` — ${meta.reason}`;
                if (meta?.ip) detail += ` from ${meta.ip}`;
                return (
                  <TimelineItem
                    key={entry.id}
                    icon={cfg.icon}
                    color={cfg.color}
                    label={cfg.label}
                    value={detail}
                  />
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Tab 2: SIP Devices ──────────────────────────────────────
function SipDevicesTab({ t }) {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRow, setExpandedRow] = useState(null);
  const [historyData, setHistoryData] = useState({});
  const [historyLoading, setHistoryLoading] = useState({});
  const [sipOrderBy, setSipOrderBy] = useState('extension');
  const [sipOrder, setSipOrder] = useState('asc');

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { limit: rowsPerPage, offset: page * rowsPerPage };
      if (search) params.search = search;
      if (activeOnly) params.isActive = 'true';

      const { data } = await telephonyApi.getSipSessions(params);
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load SIP sessions');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, activeOnly]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const fetchHistory = useCallback(async (sessionId) => {
    if (historyData[sessionId]) return;
    setHistoryLoading(prev => ({ ...prev, [sessionId]: true }));
    try {
      const { data } = await telephonyApi.getSipSessionHistory(sessionId, { limit: 50 });
      setHistoryData(prev => ({ ...prev, [sessionId]: data.history || [] }));
    } catch {
      setHistoryData(prev => ({ ...prev, [sessionId]: [] }));
    } finally {
      setHistoryLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  }, [historyData]);

  const getStatusChip = (session) => {
    const now = new Date();
    const expires = session.expiresAt ? new Date(session.expiresAt) : null;

    if (!session.isActive) {
      return <Chip label="Expired" size="small" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600, fontSize: '0.75rem' }} />;
    }
    if (expires && expires < now) {
      return <Chip label="Expired" size="small" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600, fontSize: '0.75rem' }} />;
    }
    if (expires) {
      const daysLeft = (expires - now) / 86400000;
      if (daysLeft < 3) {
        return <Chip label="Expiring" size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600, fontSize: '0.75rem' }} />;
      }
    }
    return <Chip label="Active" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.75rem' }} />;
  };

  const handleSipSort = (column) => {
    const isAsc = sipOrderBy === column && sipOrder === 'asc';
    setSipOrder(isAsc ? 'desc' : 'asc');
    setSipOrderBy(column);
  };

  const sortedSessions = useMemo(() => {
    if (!sipOrderBy) return sessions;
    return [...sessions].sort((a, b) => {
      let aVal = a[sipOrderBy];
      let bVal = b[sipOrderBy];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (sipOrderBy === 'createdAt' || sipOrderBy === 'lastRegisteredAt' || sipOrderBy === 'expiresAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return sipOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sipOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sessions, sipOrderBy, sipOrder]);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t ? t('Telephony.search_sip') : 'Search extension, person, device...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); fetchSessions(); } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
          sx={{ minWidth: 260 }}
        />
        <FormControlLabel
          control={<Switch checked={activeOnly} onChange={(e) => { setActiveOnly(e.target.checked); setPage(0); }} />}
          label={t ? t('Telephony.active_only') : 'Active only'}
        />
        <IconButton onClick={() => { setPage(0); fetchSessions(); }} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sortDirection={sipOrderBy === 'extension' ? sipOrder : false}>
                <TableSortLabel active={sipOrderBy === 'extension'} direction={sipOrderBy === 'extension' ? sipOrder : 'asc'} onClick={() => handleSipSort('extension')}>
                  Extension
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sipOrderBy === 'personName' ? sipOrder : false}>
                <TableSortLabel active={sipOrderBy === 'personName'} direction={sipOrderBy === 'personName' ? sipOrder : 'asc'} onClick={() => handleSipSort('personName')}>
                  Person
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sipOrderBy === 'deviceName' ? sipOrder : false}>
                <TableSortLabel active={sipOrderBy === 'deviceName'} direction={sipOrderBy === 'deviceName' ? sipOrder : 'asc'} onClick={() => handleSipSort('deviceName')}>
                  Device
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell sortDirection={sipOrderBy === 'createdAt' ? sipOrder : false}>
                <TableSortLabel active={sipOrderBy === 'createdAt'} direction={sipOrderBy === 'createdAt' ? sipOrder : 'asc'} onClick={() => handleSipSort('createdAt')}>
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sipOrderBy === 'lastRegisteredAt' ? sipOrder : false}>
                <TableSortLabel active={sipOrderBy === 'lastRegisteredAt'} direction={sipOrderBy === 'lastRegisteredAt' ? sipOrder : 'asc'} onClick={() => handleSipSort('lastRegisteredAt')}>
                  Last Registered
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sipOrderBy === 'expiresAt' ? sipOrder : false}>
                <TableSortLabel active={sipOrderBy === 'expiresAt'} direction={sipOrderBy === 'expiresAt' ? sipOrder : 'asc'} onClick={() => handleSipSort('expiresAt')}>
                  Expires
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : sortedSessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No SIP sessions found
                </TableCell>
              </TableRow>
            ) : sortedSessions.map((s) => {
              const isExpanded = expandedRow === s.id;
              return (
                <React.Fragment key={s.id}>
                  <TableRow
                    hover
                    onClick={() => {
                      if (isExpanded) {
                        setExpandedRow(null);
                      } else {
                        setExpandedRow(s.id);
                        fetchHistory(s.id);
                      }
                    }}
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                  >
                    <TableCell sx={{ width: 40, px: 1 }}>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{s.extension}</TableCell>
                    <TableCell>{s.personName || '-'}</TableCell>
                    <TableCell>{s.deviceName || '-'}</TableCell>
                    <TableCell>{getStatusChip(s)}</TableCell>
                    <TableCell sx={{ fontSize: '0.82rem' }}>
                      {s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem' }}>
                      {s.lastRegisteredAt ? new Date(s.lastRegisteredAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem' }}>
                      {s.expiresAt ? new Date(s.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, px: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ bgcolor: 'action.hover', px: 3, py: 0 }}>
                          <SipSessionDetailRow
                            session={s}
                            history={historyData[s.id] || []}
                            loading={historyLoading[s.id]}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </>
  );
}

// ─── Tab 3: Overview ─────────────────────────────────────────
function OverviewTab({ t }) {
  const [stats, setStats] = useState({ callsToday: 0, callsWeek: 0, missedToday: 0, activeSip: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString();

        const [todayRes, weekRes, missedRes, sipRes] = await Promise.all([
          telephonyApi.getCallLogs({ startDate: todayStart, limit: 1 }),
          telephonyApi.getCallLogs({ startDate: weekStart, limit: 1 }),
          telephonyApi.getCallLogs({ startDate: todayStart, status: 'missed', limit: 1 }),
          telephonyApi.getSipSessions({ isActive: 'true', limit: 1 }),
        ]);

        setStats({
          callsToday: todayRes.data.total || 0,
          callsWeek: weekRes.data.total || 0,
          missedToday: missedRes.data.total || 0,
          activeSip: sipRes.data.total || 0,
        });
      } catch {
        // Stats are best-effort
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const cards = [
    { label: t ? t('Telephony.calls_today') : 'Calls Today', value: stats.callsToday, icon: <PhoneIcon />, color: '#1565C0' },
    { label: t ? t('Telephony.calls_week') : 'Calls This Week', value: stats.callsWeek, icon: <PhoneIcon />, color: '#7B1FA2' },
    { label: t ? t('Telephony.missed_today') : 'Missed Today', value: stats.missedToday, icon: <MissedIcon />, color: '#C62828' },
    { label: t ? t('Telephony.active_sip') : 'Active SIP Sessions', value: stats.activeSip, icon: <SipIcon />, color: '#2E7D32' },
  ];

  return (
    <Grid container spacing={3}>
      {cards.map((c) => (
        <Grid item xs={12} sm={6} md={3} key={c.label}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ color: c.color, mb: 1 }}>{c.icon}</Box>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h3" sx={{ fontWeight: 700, color: c.color }}>
                  {c.value}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {c.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
